import { Calendar, Popover } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import React, { useEffect, useState } from "react";

import { CalendarArrow } from "@/Components/Icons/CalendarArrow";
import { CalendarIcon } from "@/Components/Icons/CalendarIcon";

/**
 * Function component for searching through a calendar.
 *
 * @param {array} val - the array of selected dates
 * @param {function} handleChange - the function to handle changes in selected dates
 * @param {string} format - the format of the dates (default is "YYYY-MM-DD")
 * @return {JSX} the calendar search component
 */
const CalendarSearch = ({
  val = [],
  handleChange = () => {},
  format = "YYYY-MM-DD",
  actMonthIndex = dayjs().month(),
  highlightDates = [],
}) => {
  const [activeMonthIndex, setActiveMonthIndex] = useState(actMonthIndex);
  useEffect(() => {
    setActiveMonthIndex(activeMonthIndex);
  }, [actMonthIndex]);
  const [activeCalender, setActiveCalender] = useState([
    dayjs().month(activeMonthIndex),
    dayjs().month(activeMonthIndex + 1 === 12 ? 0 : activeMonthIndex + 1),
  ]);

  const [activeMonth, setActiveMonth] = useState(0);
  /**
   * Retrieve the name of the month based on the given month index.
   *
   * @param {number} monthIndex - The index of the month (0-11).
   * @return {string} The name of the month in abbreviated format (e.g. Jan, Feb, Mar).
   */
  const getMonthName = (monthIndex) => {
    let monthName = moment().month(monthIndex).format("MMM");
    return monthName;
  };
  const [selectedDates, setSelectedDates] = useState(val);
  /**
   * This function handles the apply action.
   *
   * @return {void}
   */
  const handleApply = () => {
    handleChange(selectedDates);
  };
  /**
   * A function that handles the reset of selected dates.
   */
  const handleReset = () => {
    setSelectedDates([]);
    handleChange([]);
  };
  return (
    <div className="CalendarSearch_Wrapper group relative pl-5">
      <Popover
        arrow={false}
        placement="left"
        content={
          <div className="!w-[371px] rounded-lg bg-white p-4">
            <Calendar
              fullscreen={false}
              value={activeCalender[activeMonth]}
              fullCellRender={(value) => {
                const date = moment(value.$d).format(format);
                const isSelected = selectedDates.includes(date);
                const isHighlighted = highlightDates.includes(date);
                // let isCurrentMonth = activeCalender[activeMonth].month() === value.$M;
                return (
                  <div
                    className={`relative grid h-[48px] w-[48px] place-items-center ${isHighlighted ? "rounded-md bg-lightblue" : "pointer-events-none !cursor-not-allowed select-none"}`}
                    onClick={() => {
                      if (isSelected) {
                        setSelectedDates(
                          selectedDates.filter((item) => item !== date),
                        );
                      } else {
                        setSelectedDates([...selectedDates, date]);
                      }
                    }}
                  >
                    <button
                      className={`h-[32px] w-[32px] rounded-lg text-[18px] font-medium leading-[32px] ${
                        isSelected ? "bg-primary text-white" : ""
                      }`}
                    >
                      {value.$D}
                    </button>
                  </div>
                );
              }}
              headerRender={({ value, type, onChange, onTypeChange }) => {
                return (
                  <>
                    <div
                      className={`relative mb-[16px] grid h-[40px] grid-cols-2 overflow-hidden rounded-lg bg-grey before:absolute before:top-0 before:z-0 before:h-full before:w-1/2 before:bg-primary before:transition-all before:duration-200 before:ease-linear before:content-[''] ${
                        activeMonth === 0 ? "before:left-0" : "before:left-1/2"
                      }`}
                    >
                      <div
                        className={`current_month relative z-[1] flex items-center gap-4 p-[8px_24px_8px_12px] transition-all duration-200 ease-linear ${
                          activeMonth === 0 ? "text-white" : ""
                        }`}
                        onClick={(e) => {
                          if (
                            e.target.tagName === "BUTTON" ||
                            e.target.tagName === "svg" ||
                            e.target.tagName === "path"
                          ) {
                            return;
                          } else {
                            setActiveMonth(0);
                          }
                        }}
                      >
                        <button
                          className="flex h-[24px] w-[24px] items-center justify-center rounded-lg !bg-white !text-txtColor"
                          onClick={() => {
                            if (activeMonth === 0) {
                              setActiveMonth(1);
                              setActiveCalender([
                                activeCalender[0].subtract(2, "month"),
                                activeCalender[1].subtract(2, "month"),
                              ]);
                            } else {
                              setActiveMonth(0);
                            }
                          }}
                        >
                          <CalendarArrow />
                        </button>
                        <div className="line-clamp-1 grow text-[16px] font-medium leading-[normal]">
                          {getMonthName(activeCalender[0].month())}{" "}
                          {activeCalender[0].year()}
                        </div>
                      </div>
                      <div
                        className={`next_month relative z-[1] flex items-center gap-4 p-[8px_12px_8px_24px] transition-all duration-200 ease-linear ${
                          activeMonth === 1 ? "text-white" : ""
                        }`}
                        onClick={(e) => {
                          if (
                            e.target.tagName === "BUTTON" ||
                            e.target.tagName === "svg" ||
                            e.target.tagName === "path"
                          ) {
                            return;
                          } else {
                            setActiveMonth(1);
                          }
                        }}
                      >
                        <div className="line-clamp-1 grow text-right text-[16px] font-medium leading-[normal]">
                          {getMonthName(activeCalender[1].month())}{" "}
                          {activeCalender[1].year()}
                        </div>
                        <button
                          className="flex h-[24px] w-[24px] -rotate-180 items-center justify-center rounded-lg !bg-white !text-txtColor"
                          onClick={() => {
                            if (activeMonth === 1) {
                              setActiveMonth(0);
                              setActiveCalender([
                                activeCalender[0].add(2, "month"),
                                activeCalender[1].add(2, "month"),
                              ]);
                            } else {
                              setActiveMonth(1);
                            }
                          }}
                        >
                          <CalendarArrow />
                        </button>
                      </div>
                    </div>
                  </>
                );
              }}
            />
            <div className="flex justify-center gap-4">
              <button
                className="transition-linear h-[36px] w-[120px] rounded-lg border border-primary bg-primary capitalize leading-[36px] text-white hover:bg-none hover:text-primary"
                onClick={() => {
                  handleApply();
                }}
              >
                Apply
              </button>
              <button
                className="transition-linear h-[36px] w-[120px] rounded-lg border border-primary bg-none capitalize leading-[36px] text-primary hover:bg-primary hover:text-white"
                onClick={() => {
                  handleReset();
                }}
              >
                Reset
              </button>
            </div>
          </div>
        }
      >
        <div
          className={`${
            val.length > 0
              ? "bg-primary text-white"
              : "hover:bg-E7EFFB bg-grey text-txtColor hover:text-blue focus:bg-blue"
          } transition-linear grid h-[40px] w-[56px] cursor-pointer place-content-center rounded-lg`}
        >
          <CalendarIcon />
        </div>
      </Popover>
    </div>
  );
};

export default CalendarSearch;
